"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.userHasSharedPermission = exports.userHasPermission = exports.hasPermission = void 0;
// TODO: Maybe make this configurable
const FULL_PERMISSION_INDICATORS = ['*', 'all', 'full'];
/**
 * Checks if given permission is found in the list of given user permissions.
 *
 * @param {string} permission - The permission to check for.
 * @param {string[]} userPermissions - The list of permissions the user has.
 * @returns {boolean} - Returns true if the user has the specified permission or a full permission indicator for the core permission, otherwise false.
 */
const hasPermission = (permission, userPermissions) => {
    let hasPerm = userPermissions.includes(permission);
    if (hasPerm) {
        return true;
    }
    const corePermission = permission.split(':')[0];
    for (const perm of userPermissions) {
        const parts = perm.split(':');
        if (parts[0] === corePermission && FULL_PERMISSION_INDICATORS.includes(parts[1])) {
            return true;
        }
    }
    return false;
};
exports.hasPermission = hasPermission;
/** Get a deduped, flat set of permissions for the given auth user. */
const allPermissions = (authUser) => {
    const permissions = new Set();
    for (const group of authUser.permissionGroups) {
        for (const permission of group.permissions) {
            permissions.add(permission);
        }
    }
    return Array.from(permissions);
};
/** Get the user's permissions for the given group. */
const groupPermissions = (authUser, groupUid) => {
    var _a;
    const group = authUser.permissionGroups.find((g) => g.id === groupUid);
    return (_a = group === null || group === void 0 ? void 0 : group.permissions) !== null && _a !== void 0 ? _a : [];
};
const sharedGroupPermissions = (authUser, groupUid) => {
    var _a;
    const group = authUser.sharedPermissionGroups.find((g) => g.id === groupUid);
    return (_a = group === null || group === void 0 ? void 0 : group.permissions) !== null && _a !== void 0 ? _a : [];
};
/**
 * Check if user has the specified permission(s).
 *
 * Normally, you should provide the related permission group ID. However, if your context does not include a group, then
 * you can provide `SkipGroupAuthorization` to bypass the group check.
 *
 * @param authUser - current user; see `getAuthUser` if you need to get it
 * @param grantingPermissions - permission(s) to check for to see if user has permission(s) explicitly, or user has a "full" permission
 * @param groupUid - group UUID to check permission(s) within a group. If `SkipGroupAuthorization` provided, with authorize if any group has the requested permission(s).
 */
const userHasPermission = (authUser, grantingPermissions, groupUid) => {
    const permissionsToCheck = Array.isArray(grantingPermissions) ? grantingPermissions : [grantingPermissions];
    const userPermissions = typeof groupUid === 'string' ? groupPermissions(authUser, groupUid) : allPermissions(authUser);
    for (const permission of permissionsToCheck) {
        if ((0, exports.hasPermission)(permission, userPermissions)) {
            return true;
        }
    }
    return false;
};
exports.userHasPermission = userHasPermission;
/**
 * Check if user has the specified permission(s) in a specific group via sharing.
 *
 * @param authUser - current user; see `getAuthUser` if you need to get it
 * @param grantingPermissions - permission(s) to check for to see if user has permission(s) explicitly, or user has a "full" permission
 * @param groupUid - group UUID to check permission(s) within a group.
 */
const userHasSharedPermission = (authUser, grantingPermissions, groupUid) => {
    const permissionsToCheck = Array.isArray(grantingPermissions) ? grantingPermissions : [grantingPermissions];
    const permissionsInGroup = sharedGroupPermissions(authUser, groupUid);
    for (const permission of permissionsToCheck) {
        if ((0, exports.hasPermission)(permission, permissionsInGroup)) {
            return true;
        }
    }
    return false;
};
exports.userHasSharedPermission = userHasSharedPermission;
